/**
 * Multi-row preferred J-code / drug-name editor for Status Update.
 *
 * Controlled by the parent so entry values can feed MultiSelect options and
 * save payloads.
 * @module PreferredJcodesInput
 */

import SingleJcodeEntryRow from "./SingleJcodeEntryRow";
import { PreferredJcodeEntry } from "./statusUpdateModal.types";
import { createEmptyPreferredJcodeEntry } from "./utils/createEmptyPreferredJcodeEntry";

export { createEmptyPreferredJcodeEntry } from "./utils/createEmptyPreferredJcodeEntry";

/** Props for {@link PreferredJcodesInput}. */
interface PreferredJcodesInputProps {
  /** Current preferred J-code / drug-name rows. */
  entries: PreferredJcodeEntry[];
  /** Replaces the full entries list after an add / edit / delete. */
  onEntriesChange: (entries: PreferredJcodeEntry[]) => void;
  /** When true, the input fields are required. */
  required?: boolean;
}

/**
 * Renders one or more preferred J-code rows with add / edit / delete support.
 *
 * The first row shows field labels; subsequent rows omit labels via
 * `hideLabels` so the group reads as a single labeled table. When `required`
 * is true, each labeled J-code and drug-name field is marked required.
 *
 * @input props.entries - Controlled preferred J-code rows
 * @input props.onEntriesChange - Callback when the rows list changes
 * @input props.required - When true, marks J-code and drug-name inputs required
 * @return JSX for the preferred J-codes input group
 */
const PreferredJcodesInput = ({
  entries,
  onEntriesChange,
  required = false,
}: PreferredJcodesInputProps) => {
  /**
   * Updates the J-code value for the row matching `id`.
   *
   * @input id - Row id to update
   * @input value - New J-code text
   */
  const handleJCodeChange = (id: string, value: string) => {
    onEntriesChange(
      entries.map((entry) =>
        entry.id === id ? { ...entry, jCode: value } : entry,
      ),
    );
  };

  /**
   * Updates the drug name value for the row matching `id`.
   *
   * @input id - Row id to update
   * @input value - New drug name text
   */
  const handleDrugNameChange = (id: string, value: string) => {
    onEntriesChange(
      entries.map((entry) =>
        entry.id === id ? { ...entry, drugName: value } : entry,
      ),
    );
  };

  /**
   * Removes the row matching `id`, keeping at least one empty row.
   *
   * @input id - Row id to remove
   */
  const handleDelete = (id: string) => {
    const next = entries.filter((entry) => entry.id !== id);
    onEntriesChange(
      next.length > 0 ? next : [createEmptyPreferredJcodeEntry()],
    );
  };

  /**
   * Appends a new blank preferred J-code row via {@link onEntriesChange}.
   *
   * @return void
   */
  const handleAddDrug = () => {
    onEntriesChange([...entries, createEmptyPreferredJcodeEntry()]);
  };

  return (
    <div className="preferred-jcodes-input--container flex flex-col gap-3 rounded-md border border-primaryGray-15 p-3">
      {entries.map((entry, index) => (
        <SingleJcodeEntryRow
          key={entry.id}
          id={entry.id}
          jCode={entry.jCode}
          drugName={entry.drugName}
          hideLabels={index > 0}
          showDelete={entries.length > 1}
          required={required}
          onJCodeChange={handleJCodeChange}
          onDrugNameChange={handleDrugNameChange}
          onDelete={handleDelete}
        />
      ))}
      <button
        type="button"
        className="add-drug-section cursor-pointer border-none bg-transparent p-0 text-left text-tiny font-semibold text-tertiaryBlue-4"
        onClick={handleAddDrug}
      >
        + Add Drug
      </button>
    </div>
  );
};

export default PreferredJcodesInput;
